import React, { useMemo, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faMapMarkerAlt, faClock } from "@fortawesome/free-solid-svg-icons";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import CustomButton from "../../Components/CustomButton";
import Loader from '../../Components/Loader';

import "./style.css";

import { useGetLocationHistoryQuery, useGetFamilyLinksQuery } from "../../Redux/Apis/Family";
import { dateFormatter } from "../../Utils";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const FitBounds = ({ positions }) => {
  const map = useMap();

  useEffect(() => {
    if (!positions.length) return;
    if (positions.length === 1) {
      map.setView(positions[0], 15);
      return;
    }
    const bounds = L.latLngBounds(positions);
    map.fitBounds(bounds, { padding: [30, 30] });
  }, [map, positions]);

  return null;
};

export const LocationHistory = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [hours, setHours] = useState(24);
  const [addressByKey, setAddressByKey] = useState({});

  // Fetch link details
  const { data: linksData, isLoading: loadingLink } = useGetFamilyLinksQuery({
    page: 1,
    limit: 100,
  });

  const link = linksData?.data?.find(l => l._id === id);

  // Fetch location history
  const { data: locationData, isLoading: loadingLocation, refetch } = useGetLocationHistoryQuery(
    { id, hours },
    { skip: !id }
  );

  const locations = locationData?.data || [];

  const getLocationKey = (lat, lng) => `${lat.toFixed(4)},${lng.toFixed(4)}`;

  const formatAddress = (displayName) => {
    if (!displayName) return "Approximate location";
    const parts = displayName.split(",").map((part) => part.trim());
    return parts.slice(0, 3).join(", ");
  };

  useEffect(() => {
    if (!locations.length) return;

    let isCancelled = false;

    const fetchAddresses = async () => {
      const nextAddressByKey = {};

      for (const loc of locations) {
        const key = getLocationKey(loc.latitude, loc.longitude);
        if (addressByKey[key]) {
          nextAddressByKey[key] = addressByKey[key];
          continue;
        }

        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${loc.latitude}&lon=${loc.longitude}&zoom=16&addressdetails=1`;
          const response = await fetch(url, {
            headers: { "Accept": "application/json" },
          });
          const data = await response.json();
          nextAddressByKey[key] = formatAddress(data?.display_name);
        } catch (error) {
          nextAddressByKey[key] = "Approximate location";
        }

        if (isCancelled) return;
      }

      if (!isCancelled && Object.keys(nextAddressByKey).length) {
        setAddressByKey((prev) => ({ ...prev, ...nextAddressByKey }));
      }
    };

    fetchAddresses();

    return () => {
      isCancelled = true;
    };
  }, [locations, addressByKey]);

  const latestLocation = useMemo(() => {
    if (!locations.length) return null;
    return [...locations].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
  }, [locations]);

  const polylinePositions = useMemo(() => {
    if (!locations.length) return [];
    return [...locations]
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      .map((loc) => [loc.latitude, loc.longitude]);
  }, [locations]);

  useEffect(() => {
    document.title = 'Landing List | Marshall History';
  }, []);

  const handleTimeRangeChange = (newHours) => {
    setHours(newHours);
  };

  if (loadingLink) {
    return (
      <DashboardLayout>
        <Loader />
      </DashboardLayout>
    );
  }

  return (
    <>
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <button className="backBtn" onClick={() => navigate(-1)}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </button>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              <div className="dashCard">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h2 className="mainTitle mb-1">Marshall Activity</h2>
                    {link?.childId && (
                      <p className="text-muted mb-0">
                        Activity history for {link.childId.name}
                      </p>
                    )}
                  </div>
                  <div className="timeRangeSelector">
                    <button
                      className={`timeBtn ${hours === 1 ? 'active' : ''}`}
                      onClick={() => handleTimeRangeChange(1)}
                    >
                      1h
                    </button>
                    <button
                      className={`timeBtn ${hours === 6 ? 'active' : ''}`}
                      onClick={() => handleTimeRangeChange(6)}
                    >
                      6h
                    </button>
                    <button
                      className={`timeBtn ${hours === 24 ? 'active' : ''}`}
                      onClick={() => handleTimeRangeChange(24)}
                    >
                      24h
                    </button>
                    <button
                      className={`timeBtn ${hours === 48 ? 'active' : ''}`}
                      onClick={() => handleTimeRangeChange(48)}
                    >
                      48h
                    </button>
                  </div>
                </div>

                {/* Map */}
                <div className="mapContainer">
                  {loadingLocation ? (
                    <div className="mapPlaceholder">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="mapIcon" />
                      <p>Loading map...</p>
                    </div>
                  ) : latestLocation ? (
                    <MapContainer
                      className="map"
                      center={[latestLocation.latitude, latestLocation.longitude]}
                      zoom={14}
                      scrollWheelZoom={true}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <FitBounds positions={polylinePositions} />
                      {polylinePositions.length > 1 && (
                        <Polyline positions={polylinePositions} pathOptions={{ color: "#28a745" }} />
                      )}
                      {locations.map((loc, index) => (
                        <Marker
                          key={loc._id || index}
                          position={[loc.latitude, loc.longitude]}
                        >
                          <Popup>
                            <div>
                              <div>{dateFormatter(loc.timestamp)}</div>
                              <div>
                                {addressByKey[getLocationKey(loc.latitude, loc.longitude)] || "Resolving address..."}
                              </div>
                              {loc.accuracy && <div>Accuracy: ±{Math.round(loc.accuracy)}m</div>}
                              {loc.speed !== null && loc.speed > 0 && (
                                <div>Speed: {Math.round(loc.speed * 3.6)} km/h</div>
                              )}
                              {loc.battery !== null && (
                                <div>Battery: {loc.battery}%</div>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </MapContainer>
                  ) : (
                    <div className="mapPlaceholder">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="mapIcon" />
                      <p>No map data available</p>
                      <p className="text-muted small">
                        Location points are listed below
                      </p>
                    </div>
                  )}
                </div>

                {/* Location List */}
                <div className="detailsCard">
                  <h4>
                    <FontAwesomeIcon icon={faClock} className="me-2" />
                    Location Points ({locationData?.data?.length || 0})
                  </h4>

                  {loadingLocation ? (
                    <p>Loading locations...</p>
                  ) : locationData?.data?.length > 0 ? (
                    <div className="locationList">
                      {locationData.data.map((loc, index) => (
                        <div key={loc._id || index} className="locationItem">
                          <div className="locationIndex">
                            {index + 1}
                          </div>
                          <div className="locationDetails">
                            <div className="locationCoords">
                              {addressByKey[getLocationKey(loc.latitude, loc.longitude)] || "Resolving address..."}
                            </div>
                            <div className="locationMeta">
                              {loc.accuracy && <span>±{Math.round(loc.accuracy)}m</span>}
                              {loc.speed !== null && loc.speed > 0 && (
                                <span> • {Math.round(loc.speed * 3.6)} km/h</span>
                              )}
                              {loc.battery !== null && (
                                <span> • Battery: {loc.battery}%</span>
                              )}
                            </div>
                          </div>
                          <div className="locationTime">
                            {dateFormatter(loc.timestamp)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="emptyLocations">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="emptyIcon" />
                      <p>No location data available for the selected time range</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>

      <style jsx>{`
        .backBtn {
          background: none;
          border: none;
          color: #28a745;
          font-size: 14px;
          cursor: pointer;
          padding: 8px 0;
        }
        .backBtn:hover {
          text-decoration: underline;
        }
        .timeRangeSelector {
          display: flex;
          gap: 8px;
        }
        .timeBtn {
          padding: 8px 16px;
          border: 1px solid #ddd;
          background: #fff;
          border-radius: 20px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }
        .timeBtn:hover {
          border-color: #28a745;
          color: #28a745;
        }
        .timeBtn.active {
          background: #28a745;
          border-color: #28a745;
          color: #fff;
        }
        .mapPlaceholder {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          color: #666;
        }
        .mapPlaceholder .mapIcon {
          font-size: 48px;
          color: #ddd;
          margin-bottom: 16px;
        }
        .locationIndex {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #28a745;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 600;
          flex-shrink: 0;
        }
        .locationDetails {
          flex: 1;
          margin-left: 16px;
        }
        .locationMeta {
          font-size: 12px;
          color: #666;
          margin-top: 4px;
        }
        .emptyLocations {
          text-align: center;
          padding: 40px;
          color: #666;
        }
        .emptyLocations .emptyIcon {
          font-size: 48px;
          color: #ddd;
          margin-bottom: 16px;
        }
      `}</style>
    </>
  );
};

export default LocationHistory;

